import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AppConstants } from 'src/app/commons/app-constants';
import { SideBarMenuService } from '../side-bar-menu.service';

const SUPER_USER_ROLES = ['SUPER_ADMIN', 'ROOT'];

/**
 * Global Process module visibility. Combines the SUPER_ADMIN/ROOT bypass (they can always
 * see/use Process, even while it's OFF for everyone else) with the Config.processModuleEnabled
 * flag, so every consumer (menu-adjacent UI, route guard) shares one visibility decision.
 * Independent of InventoryModuleService — Process can be enabled without Inventory and vice versa.
 */
@Injectable({ providedIn: 'root' })
export class ProcessModuleService {
  private readonly visibleSubject = new BehaviorSubject<boolean>(false);
  readonly visible$ = this.visibleSubject.asObservable();

  private loaded = false;
  private resolved = false;

  constructor(private http: HttpClient, private sideBarMenu: SideBarMenuService) {}

  get visible(): boolean {
    return this.visibleSubject.value;
  }

  /**
   * True once a real answer has come back — NOT merely once load() was called.
   *
   * The distinction matters: load() flips `loaded` immediately, before the HTTP call returns,
   * while `visible` stays at its default of false. A synchronous caller reading `visible` in that
   * window would conclude the Mill module is unavailable and hide it from a Forest user who is
   * paying for it. Callers that cannot wait should treat "not yet resolved" as "assume available"
   * and let ProcessModuleGuard (a fresh uncached check) or the backend make the real decision.
   */
  get isResolved(): boolean {
    return this.resolved;
  }

  load(): void {
    if (this.loaded) return;
    this.loaded = true;
    this.checkVisible().subscribe((v) => {
      this.resolved = true;
      this.visibleSubject.next(v);
    });
  }

  refresh(): void {
    this.loaded = false;
    this.resolved = false;
    this.load();
  }

  /** Fresh, uncached check — used by the route guard to avoid a cold-start race against the default-false value. */
  checkVisible(): Observable<boolean> {
    return this.isSuperUser$().pipe(
      switchMap((isSuperUser) => {
        if (isSuperUser) return of(true);
        return this.http
          .get<{ processModuleEnabled: boolean }>(`${AppConstants.API_URL}api/v1/app/process-module`)
          .pipe(
            map((res) => res.processModuleEnabled),
            catchError(() => of(true)), // network error → allow; matches PermissionGuard's convention
          );
      }),
    );
  }

  private isSuperUser$(): Observable<boolean> {
    return from(this.getUserProfile()).pipe(
      map((profile: any) => {
        const roles: string[] = profile?.roles || [];
        return roles.some((r) => SUPER_USER_ROLES.includes((r || '').replace(/^ROLE_/, '')));
      }),
      catchError(() => of(false)),
    );
  }

  private async getUserProfile(): Promise<any> {
    const cached = await this.sideBarMenu.getDecryptedUserProfile();
    if (cached) return cached;
    return new Promise((resolve, reject) => {
      this.sideBarMenu.getUserProfile().subscribe({
        next: (profile: any) => resolve(profile),
        error: reject,
      });
    });
  }
}
